import '../../../Components/Form/form.css'

import { motion } from 'framer-motion'
import { useEffect, useState } from 'react'

import { cap } from '../../../State'
import { translations } from '../../../translations'
import { Icon } from './Icons'
import { useGigiFormValidation } from './ValidationContext'

const language = cap.language.state || 'IT'
const t = translations[language]
import './section-shell.css'

const useCollapseState = (key, initialCollapsed = false) => {
  const [collapsed, setCollapsed] = useState(() => {
    if (typeof window === 'undefined') return initialCollapsed
    if (!key) return initialCollapsed
    const stored = window.sessionStorage?.getItem(key)
    return stored === null || stored === undefined ? initialCollapsed : stored === '1'
  })
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!key) return
    window.sessionStorage?.setItem(key, collapsed ? '1' : '0')
  }, [collapsed, key])
  return [collapsed, setCollapsed]
}

export const SectionShell = ({
  id,
  icon,
  title,
  storageKey,
  defaultCollapsed = false,
  count,
  countLabel,
  completionLabel,
  summary,
  headerExtra,
  contentId,
  hasErrors: hasErrorsProp,
  children,
  noPadding = false
}) => {
  const { sectionHasErrors, sectionErrorCount } = useGigiFormValidation()
  const hasErrors = id ? sectionHasErrors(id) : !!hasErrorsProp
  const errorCount = id ? sectionErrorCount(id) : 0
  const [collapsed, setCollapsed] = useCollapseState(storageKey, defaultCollapsed)
  const innerId = contentId || (storageKey ? `${storageKey}-content` : undefined)
  const toggle = () => setCollapsed((c) => !c)

  return (
    <section
      className={`gf-card${hasErrors ? ' has-error' : ''}`}
      data-open={!collapsed}
      id={id ? `section-${id}` : undefined}
    >
      <header
        className="gf-card__head"
        onClick={toggle}
        role="button"
        tabIndex={0}
        aria-expanded={!collapsed}
        aria-controls={innerId}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            toggle()
          }
        }}
      >
        {icon && (
          <div className="gf-card__ico">
            <Icon name={icon} size={16} />
          </div>
        )}
        <div className="gf-card__titles">
          <h3 className="gf-card__title">{title}</h3>
          {collapsed && summary && <div className="gf-card__sub">{summary}</div>}
        </div>
        <div className="gf-card__spacer" />
        <div className="gf-card__head-actions" onClick={(e) => e.stopPropagation()}>
          {hasErrors && (
            <span className="gf-badge gf-badge--err">
              <Icon name="alert" size={11} />{' '}
              {errorCount > 0
                ? `${errorCount} ${errorCount === 1 ? t.gfCampo : t.gfCampi} ${t.gfCampiDaRivedere}`
                : countLabel || t.gfDaRivedere}
            </span>
          )}
          {!hasErrors && completionLabel && (
            <span className="gf-badge gf-badge--ok">
              <Icon name="check" size={11} /> {completionLabel}
            </span>
          )}
          {typeof count === 'number' && count > 0 && !hasErrors && !completionLabel && (
            <span className="gf-badge gf-badge--ok">
              {count} {count === 1 ? t.gfServizio : t.gfServizi}
            </span>
          )}
          {typeof count === 'number' && count > 0 && hasErrors && (
            <span className="gf-badge">
              {count} {count === 1 ? t.gfServizio : t.gfServizi}
            </span>
          )}
          {typeof count === 'number' && count === 0 && !hasErrors && (
            <span className="gf-badge">{t.gfVuoto}</span>
          )}
          {headerExtra && <div className="gf-card__extra">{headerExtra}</div>}
          <button
            type="button"
            className="gf-card__chev"
            aria-label={collapsed ? t.gfEspandi : t.gfComprimi}
            onClick={(e) => {
              e.stopPropagation()
              toggle()
            }}
          >
            <motion.span animate={{ rotate: collapsed ? -90 : 0 }} transition={{ duration: 0.18 }}>
              <Icon name="chev_down" size={16} />
            </motion.span>
          </button>
        </div>
      </header>
      {/* Children stay mounted at all times — collapsing only animates
          height/opacity so each section's validation hooks keep running.
          Without this, closing a section would remove the .p-invalid
          elements that errors are derived from. */}
      <motion.div
        initial={false}
        animate={{ height: collapsed ? 0 : 'auto', opacity: collapsed ? 0 : 1 }}
        transition={{ duration: 0.22 }}
        style={{ overflow: 'hidden' }}
        aria-hidden={collapsed}
      >
        <div id={innerId} className={`gf-card__body${noPadding ? ' gf-card__body--flush' : ''}`}>
          {children}
        </div>
      </motion.div>
    </section>
  )
}
